import React, { useState } from "react";
import { useFindMany, useAction } from "@gadgetinc/react";
import { api } from "../../../api";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
  SimpleGrid,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { FiTrash2, FiPlus } from "react-icons/fi";

function SemesterManager({ cohortId, institution, cohortStartDate, cohortEndDate, cohortType }) {
  const toast = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSemester, setNewSemester] = useState({ name: "", startDate: "", endDate: "" });

  const [{ data: semesters, fetching: loading, error }, refetch] = useFindMany(api.cohortSemester, {
    filter: { cohortId: { equals: cohortId } },
    sort: { sortOrder: "Ascending" },
    select: {
      id: true,
      name: true,
      startDate: true,
      endDate: true,
      sortOrder: true,
      isActive: true,
    },
    pause: !cohortId,
  });

  const [, createSemester] = useAction(api.cohortSemester.create);
  const [, deleteSemester] = useAction(api.cohortSemester.delete);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : "N/A";
  const toDateInput = (d) => d ? new Date(d).toISOString().split("T")[0] : "";

  const handleAutoSuggest = () => {
    if (!cohortStartDate || !cohortEndDate) {
      toast({ title: "Set cohort dates first", status: "warning", duration: 3000 });
      return;
    }

    const start = new Date(cohortStartDate);
    const end = new Date(cohortEndDate);
    const totalMs = end.getTime() - start.getTime();

    const isParamedic = cohortType === "Paramedic" || cohortType === "Field_Internship";
    const count = isParamedic ? 3 : 1;
    const segmentMs = totalMs / count;
    const labels = isParamedic
      ? ["Semester 1 - Didactic", "Semester 2 - Clinical", "Semester 3 - Field Internship"]
      : ["Full Program"];

    const suggested = [];
    for (let i = 0; i < count; i++) {
      const segStart = new Date(start.getTime() + segmentMs * i);
      const segEnd = i === count - 1
        ? new Date(end)
        : new Date(start.getTime() + segmentMs * (i + 1) - 1);
      suggested.push({
        name: labels[i],
        startDate: segStart.toISOString().split("T")[0],
        endDate: segEnd.toISOString().split("T")[0],
      });
    }

    createSuggestedSemesters(suggested);
  };

  const createSuggestedSemesters = async (suggestions) => {
    try {
      for (let i = 0; i < suggestions.length; i++) {
        const s = suggestions[i];
        await createSemester({
          cohort: { _link: cohortId },
          name: s.name,
          startDate: new Date(s.startDate + "T00:00:00.000Z").toISOString(),
          endDate: new Date(s.endDate + "T23:59:59.999Z").toISOString(),
          sortOrder: i,
          institution: institution,
          isActive: true,
        });
      }
      toast({ title: "Semesters created", status: "success", duration: 3000 });
      refetch();
    } catch (err) {
      toast({ title: "Error creating semesters", description: err.message, status: "error", duration: 5000 });
    }
  };

  const handleAddSemester = async () => {
    if (!newSemester.name || !newSemester.startDate || !newSemester.endDate) {
      toast({ title: "All fields required", status: "warning", duration: 3000 });
      return;
    }
    if (new Date(newSemester.endDate) <= new Date(newSemester.startDate)) {
      toast({ title: "End date must be after start date", status: "warning", duration: 3000 });
      return;
    }

    try {
      await createSemester({
        cohort: { _link: cohortId },
        name: newSemester.name,
        startDate: new Date(newSemester.startDate + "T00:00:00.000Z").toISOString(),
        endDate: new Date(newSemester.endDate + "T23:59:59.999Z").toISOString(),
        sortOrder: semesters?.length || 0,
        institution: institution,
        isActive: true,
      });
      toast({ title: "Semester added", status: "success", duration: 3000 });
      setNewSemester({ name: "", startDate: "", endDate: "" });
      setShowAddForm(false);
      refetch();
    } catch (err) {
      toast({ title: "Error adding semester", description: err.message, status: "error", duration: 5000 });
    }
  };

  const handleDelete = async (semId, semName) => {
    if (!confirm(`Delete semester "${semName}"?`)) return;
    try {
      await deleteSemester({ id: semId });
      toast({ title: "Semester deleted", status: "success", duration: 3000 });
      refetch();
    } catch (err) {
      toast({ title: "Error deleting semester", description: err.message, status: "error", duration: 5000 });
    }
  };

  if (!cohortId) return null;

  return (
    <Box>
      {loading && <Spinner size="sm" />}
      {error && (
        <Alert status="error" size="sm">
          <AlertIcon />
          <Text fontSize="sm">{error.message}</Text>
        </Alert>
      )}

      {semesters && semesters.length > 0 ? (
        <VStack spacing={2} align="stretch">
          {semesters.map((sem) => (
            <HStack key={sem.id} justify="space-between" p={2} bg="gray.700" borderRadius="md">
              <VStack align="start" spacing={0}>
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" color="white">{sem.name}</Text>
                  {sem.isActive && <Badge colorScheme="green" size="sm">Active</Badge>}
                </HStack>
                <Text fontSize="xs" color="gray.400">
                  {formatDate(sem.startDate)} — {formatDate(sem.endDate)}
                </Text>
              </VStack>
              <IconButton
                icon={<FiTrash2 />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                onClick={() => handleDelete(sem.id, sem.name)}
                aria-label="Delete semester"
              />
            </HStack>
          ))}
        </VStack>
      ) : (
        !loading && (
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" color="gray.400">No semesters defined yet.</Text>
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              onClick={handleAutoSuggest}
            >
              {cohortType === "Paramedic" || cohortType === "Field_Internship"
                ? "Auto-create 3 semesters"
                : "Auto-create 1 semester"}
            </Button>
          </VStack>
        )
      )}

      <Divider my={3} borderColor="gray.600" />

      {showAddForm ? (
        <VStack spacing={3} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs">Name</FormLabel>
              <Input
                size="sm"
                value={newSemester.name}
                onChange={(e) => setNewSemester({ ...newSemester, name: e.target.value })}
                placeholder="e.g., Semester 1"
                bg="gray.700"
                borderColor="gray.600"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Start</FormLabel>
              <Input
                size="sm"
                type="date"
                value={newSemester.startDate}
                onChange={(e) => setNewSemester({ ...newSemester, startDate: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">End</FormLabel>
              <Input
                size="sm"
                type="date"
                value={newSemester.endDate}
                onChange={(e) => setNewSemester({ ...newSemester, endDate: e.target.value })}
                bg="gray.700"
                borderColor="gray.600"
              />
            </FormControl>
          </SimpleGrid>
          <HStack>
            <Button size="sm" colorScheme="green" onClick={handleAddSemester}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>Cancel</Button>
          </HStack>
        </VStack>
      ) : (
        <Button
          size="sm"
          leftIcon={<FiPlus />}
          variant="ghost"
          colorScheme="blue"
          onClick={() => setShowAddForm(true)}
        >
          Add Semester
        </Button>
      )}
    </Box>
  );
}

export default SemesterManager;
